// API Configuration
export const API_CONFIG = {
  BASE_URL: import.meta.env.VITE_API_URL || 'http://localhost:8000/api',
  TIMEOUT: 30000,
  RETRY_ATTEMPTS: 3,
  RETRY_DELAY: 1000,
} as const;

// Authentication Configuration
export const AUTH_CONFIG = {
  TOKEN_KEY: 'bois_access_token',
  REFRESH_TOKEN_KEY: 'bois_refresh_token',
  USER_KEY: 'bois_user',
  TOKEN_REFRESH_THRESHOLD: 5 * 60 * 1000, // 5 minutes before expiry
  SESSION_TIMEOUT: 30 * 60 * 1000, // 30 minutes of inactivity
} as const;

// Route Paths
export const ROUTES = {
  // Public routes
  HOME: '/',
  LOGIN: '/login',
  REGISTER: '/register',
  FORGOT_PASSWORD: '/forgot-password',
  RESET_PASSWORD: '/reset-password',
  VERIFY_EMAIL: '/verify-email',

  // Protected routes
  DASHBOARD: '/dashboard',
  PROFILE: '/profile',
  SETTINGS: '/settings',

  // Events routes
  EVENTS: '/events',
  EVENT_DETAIL: '/events/:id',
  EVENT_CREATE: '/events/create',
  EVENT_EDIT: '/events/:id/edit',

  // Birthdays routes
  BIRTHDAYS: '/birthdays',
  BIRTHDAY_SETTINGS: '/birthdays/settings',

  // Expenses routes (GroupPay)
  EXPENSES: '/expenses',
  EXPENSE_DETAIL: '/expenses/:id',
  EXPENSE_CREATE: '/expenses/new',
  EXPENSE_BALANCES: '/expenses/balances',

  // Transfers routes
  TRANSFERS: '/transfers',

  // Gifts routes (Wishlists & Secret Santa)
  GIFTS: '/gifts',
  WISHLISTS: '/gifts/wishlists',
  MY_WISHLIST: '/gifts/wishlists/me',
  USER_WISHLIST: '/gifts/wishlists/:userId',
  SECRET_SANTA: '/gifts/secretsanta',
  SECRET_SANTA_CREATE: '/gifts/secretsanta/new',
  SECRET_SANTA_DETAIL: '/gifts/secretsanta/:id',

  // Feature routes
  PROJECTS: '/projects',
  PROJECT_DETAIL: '/projects/:id',
  TASKS: '/tasks',
  TASK_DETAIL: '/tasks/:id',
  TEAM: '/team',
  TEAM_MEMBER: '/team/:id',
  REPORTS: '/reports',
  ANALYTICS: '/analytics',
  CALENDAR: '/calendar',
  MESSAGES: '/messages',
  NOTIFICATIONS: '/notifications',

  // Admin routes
  ADMIN: '/admin',
  ADMIN_USERS: '/admin/users',
  ADMIN_ROLES: '/admin/roles',
  ADMIN_SETTINGS: '/admin/settings',
  ADMIN_LOGS: '/admin/logs',

  // Traffic Index routes
  TRAFFIC_INDEX: '/verkehrsindex',
  TRAFFIC_INDEX_CREATE: '/verkehrsindex/create',
  TRAFFIC_INDEX_DETAIL: '/verkehrsindex/:id',

  // XP & Leaderboard routes
  LEADERBOARD: '/leaderboard',

  // Feature Requests routes
  FEATURE_REQUESTS: '/feature-requests',
  FEATURE_REQUEST_DETAIL: '/feature-requests/:id',
  FEATURE_REQUEST_CREATE: '/feature-requests/create',

  // Video Watchlist routes
  WATCHLIST: '/watchlist',
  WATCHLIST_DETAIL: '/watchlist/:id',
  WATCHLIST_CREATE: '/watchlist/create',

  // Media Recommendations routes (Serien & Filme)
  MEDIA_RECOMMENDATIONS: '/serien-filme',
  MEDIA_RECOMMENDATION_DETAIL: '/serien-filme/:id',
  MEDIA_RECOMMENDATION_CREATE: '/serien-filme/create',

  // Error routes
  NOT_FOUND: '/404',
  SERVER_ERROR: '/500',
  FORBIDDEN: '/403',
} as const;

// Query Keys for TanStack Query
export const QUERY_KEYS = {
  // Auth
  AUTH_USER: ['auth', 'user'],
  AUTH_SESSION: ['auth', 'session'],

  // Users
  USERS: ['users'],
  USER: (id: string) => ['users', id],
  USER_PROFILE: ['users', 'profile'],
  USER_SESSIONS: ['users', 'sessions'],
  USER_ACTIVITY: (id: string) => ['users', id, 'activity'],

  // Events
  EVENTS: ['events'],
  EVENT: (id: string) => ['events', id],
  EVENT_COMMENTS: (id: string) => ['events', id, 'comments'],

  // Birthdays
  BIRTHDAYS: ['birthdays'],
  BIRTHDAYS_UPCOMING: ['birthdays', 'upcoming'],
  BIRTHDAYS_THIS_MONTH: ['birthdays', 'this-month'],
  BIRTHDAYS_THIS_WEEK: ['birthdays', 'this-week'],
  MY_BIRTHDAY: ['birthdays', 'me'],

  // Expenses
  EXPENSES: ['expenses'],
  EXPENSE: (id: string) => ['expenses', id],
  EXPENSES_BALANCES: ['expenses', 'balances'],
  EXPENSES_MY_BALANCE: ['expenses', 'my-balance'],

  // Projects
  PROJECTS: ['projects'],
  PROJECT: (id: string) => ['projects', id],
  PROJECT_TASKS: (id: string) => ['projects', id, 'tasks'],
  PROJECT_MEMBERS: (id: string) => ['projects', id, 'members'],

  // Tasks
  TASKS: ['tasks'],
  TASK: (id: string) => ['tasks', id],
  TASK_COMMENTS: (id: string) => ['tasks', id, 'comments'],

  // Team
  TEAM: ['team'],
  TEAM_MEMBER: (id: string) => ['team', id],

  // Reports
  REPORTS: ['reports'],
  REPORT: (id: string) => ['reports', id],

  // Analytics
  ANALYTICS: ['analytics'],
  ANALYTICS_DASHBOARD: ['analytics', 'dashboard'],

  // Notifications
  NOTIFICATIONS: ['notifications'],
  NOTIFICATIONS_UNREAD: ['notifications', 'unread'],

  // Settings
  SETTINGS: ['settings'],

  // Traffic Index
  TRAFFIC_INDEXES: ['trafficIndexes'],
  TRAFFIC_INDEX: (id: string) => ['trafficIndexes', id],
} as const;

// Local Storage Keys
export const STORAGE_KEYS = {
  THEME: 'bois_theme',
  SIDEBAR_COLLAPSED: 'bois_sidebar_collapsed',
  LANGUAGE: 'bois_language',
  RECENT_SEARCHES: 'bois_recent_searches',
  DASHBOARD_LAYOUT: 'bois_dashboard_layout',
} as const;

// Theme Colors (matching Tailwind config)
export const COLORS = {
  primary: {
    DEFAULT: '#00D9FF',
    50: '#E5FBFF',
    100: '#CCF7FF',
    200: '#99EFFF',
    300: '#66E7FF',
    400: '#33DFFF',
    500: '#00D9FF',
    600: '#00ADCC',
    700: '#008299',
    800: '#005666',
    900: '#002B33',
  },
  secondary: {
    DEFAULT: '#B026FF',
    50: '#F5E5FF',
    100: '#EBCCFF',
    200: '#D799FF',
    300: '#C366FF',
    400: '#B026FF',
    500: '#9600E6',
    600: '#7800B3',
    700: '#5A0080',
    800: '#3C004D',
    900: '#1E001A',
  },
  accent: {
    DEFAULT: '#39FF14',
    50: '#E7FFE5',
    100: '#CFFFCC',
    200: '#9FFF99',
    300: '#6FFF66',
    400: '#39FF14',
    500: '#2ECC10',
    600: '#24990C',
    700: '#1A6608',
    800: '#103304',
    900: '#081A02',
  },
  background: {
    darkest: '#0A0E27',
    dark: '#151932',
    medium: '#1E2439',
    light: '#252B47',
  },
  text: {
    primary: '#FFFFFF',
    secondary: '#B4B8D4',
    muted: '#6B7199',
  },
  status: {
    error: '#FF4757',
    success: '#2ED573',
    warning: '#FFA502',
    info: '#00D9FF',
  },
} as const;

// Breakpoints (matching Tailwind defaults)
export const BREAKPOINTS = {
  xs: 0,
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1536,
} as const;

// Animation Durations
export const ANIMATION = {
  fast: 150,
  normal: 200,
  slow: 300,
  slower: 500,
} as const;

// Pagination Defaults
export const PAGINATION = {
  DEFAULT_PAGE: 1,
  DEFAULT_LIMIT: 10,
  LIMITS: [10, 25, 50, 100],
  MAX_LIMIT: 100,
} as const;

// Date Format Options
export const DATE_FORMATS = {
  SHORT: 'MMM d, yyyy',
  LONG: 'MMMM d, yyyy',
  FULL: 'EEEE, MMMM d, yyyy',
  TIME: 'h:mm a',
  TIME_24: 'HH:mm',
  DATETIME: 'MMM d, yyyy h:mm a',
  DATETIME_24: 'MMM d, yyyy HH:mm',
  ISO: "yyyy-MM-dd'T'HH:mm:ss.SSSxxx",
  RELATIVE: 'relative',
} as const;

// File Upload Limits
export const FILE_UPLOAD = {
  MAX_SIZE: 10 * 1024 * 1024, // 10MB
  MAX_FILES: 10,
  ACCEPTED_IMAGE_TYPES: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
  ACCEPTED_DOCUMENT_TYPES: [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ],
} as const;

// Form Validation Limits
export const VALIDATION = {
  USERNAME_MIN: 3,
  USERNAME_MAX: 30,
  PASSWORD_MIN: 8,
  PASSWORD_MAX: 128,
  NAME_MIN: 1,
  NAME_MAX: 50,
  EMAIL_MAX: 255,
  BIO_MAX: 500,
  MESSAGE_MAX: 5000,
} as const;

// Error Messages
export const ERROR_MESSAGES = {
  NETWORK_ERROR: 'Unable to connect to the server. Please check your internet connection.',
  UNAUTHORIZED: 'Your session has expired. Please log in again.',
  FORBIDDEN: 'You do not have permission to perform this action.',
  NOT_FOUND: 'The requested resource was not found.',
  SERVER_ERROR: 'An unexpected error occurred. Please try again later.',
  VALIDATION_ERROR: 'Please check your input and try again.',
  RATE_LIMIT: 'Too many requests. Please wait a moment and try again.',
} as const;

// Success Messages
export const SUCCESS_MESSAGES = {
  LOGIN: 'Welcome back!',
  LOGOUT: 'You have been logged out successfully.',
  REGISTER: 'Account created successfully. Please check your email to verify your account.',
  PASSWORD_RESET_REQUEST: 'If an account exists with that email, you will receive a password reset link.',
  PASSWORD_RESET: 'Your password has been reset successfully.',
  PROFILE_UPDATE: 'Your profile has been updated successfully.',
  SETTINGS_UPDATE: 'Your settings have been saved.',
  BIRTHDAY_SAVED: 'Dein Geburtstag wurde gespeichert.',
  BIRTHDAY_DELETED: 'Dein Geburtstag wurde gelöscht.',
  EXPENSE_CREATED: 'Ausgabe wurde erstellt.',
  EXPENSE_DELETED: 'Ausgabe wurde gelöscht.',
  SETTLED_UP: 'Schuld wurde ausgeglichen.',
} as const;

// User Roles with Display Names
export const USER_ROLES = {
  admin: { label: 'Administrator', color: 'primary' },
  manager: { label: 'Manager', color: 'secondary' },
  employee: { label: 'Employee', color: 'accent' },
  viewer: { label: 'Viewer', color: 'muted' },
} as const;

// User Status with Display Info
export const USER_STATUS = {
  active: { label: 'Active', color: 'success' },
  inactive: { label: 'Inactive', color: 'muted' },
  pending: { label: 'Pending', color: 'warning' },
  suspended: { label: 'Suspended', color: 'error' },
} as const;

// Event Categories with Display Info - must match backend enum
export const EVENT_CATEGORIES = {
  PARTY: { label: 'Party', color: 'purple', bgColor: 'bg-purple-500/10', textColor: 'text-purple-500', borderColor: 'border-purple-500/30' },
  GAMING: { label: 'Spieleabend', color: 'yellow', bgColor: 'bg-yellow-500/10', textColor: 'text-yellow-500', borderColor: 'border-yellow-500/30' },
  SPORT: { label: 'Sport', color: 'green', bgColor: 'bg-green-500/10', textColor: 'text-green-500', borderColor: 'border-green-500/30' },
  FOOD: { label: 'Essen', color: 'orange', bgColor: 'bg-orange-500/10', textColor: 'text-orange-500', borderColor: 'border-orange-500/30' },
  OTHER: { label: 'Sonstiges', color: 'gray', bgColor: 'bg-gray-500/10', textColor: 'text-gray-500', borderColor: 'border-gray-500/30' },
} as const;

// Expense Categories with Display Info - keys must match backend enum (uppercase)
export const EXPENSE_CATEGORIES = {
  FOOD: { label: 'Essen', icon: '🍕', color: 'orange', bgColor: 'bg-orange-500/10', textColor: 'text-orange-500', borderColor: 'border-orange-500/30' },
  TRANSPORT: { label: 'Transport', icon: '🚗', color: 'blue', bgColor: 'bg-blue-500/10', textColor: 'text-blue-500', borderColor: 'border-blue-500/30' },
  ACCOMMODATION: { label: 'Unterkunft', icon: '🏨', color: 'purple', bgColor: 'bg-purple-500/10', textColor: 'text-purple-500', borderColor: 'border-purple-500/30' },
  ENTERTAINMENT: { label: 'Unterhaltung', icon: '🎮', color: 'green', bgColor: 'bg-green-500/10', textColor: 'text-green-500', borderColor: 'border-green-500/30' },
  OTHER: { label: 'Sonstiges', icon: '📦', color: 'gray', bgColor: 'bg-gray-500/10', textColor: 'text-gray-500', borderColor: 'border-gray-500/30' },
} as const;

// Participant Status with Display Info
export const PARTICIPANT_STATUS = {
  PENDING: { label: 'Ausstehend', color: 'yellow', bgColor: 'bg-yellow-500/10', textColor: 'text-yellow-500' },
  ACCEPTED: { label: 'Zugesagt', color: 'green', bgColor: 'bg-green-500/10', textColor: 'text-green-500' },
  DECLINED: { label: 'Abgesagt', color: 'red', bgColor: 'bg-red-500/10', textColor: 'text-red-500' },
  MAYBE: { label: 'Vielleicht', color: 'blue', bgColor: 'bg-blue-500/10', textColor: 'text-blue-500' },
} as const;

// Feature Request Status with Display Info
export const FEATURE_REQUEST_STATUS = {
  OPEN: { label: 'Offen', color: 'blue', bgColor: 'bg-blue-500/10', textColor: 'text-blue-500', borderColor: 'border-blue-500/30', icon: '📋' },
  IN_PROGRESS: { label: 'In Bearbeitung', color: 'yellow', bgColor: 'bg-yellow-500/10', textColor: 'text-yellow-500', borderColor: 'border-yellow-500/30', icon: '🔧' },
  COMPLETED: { label: 'Umgesetzt', color: 'green', bgColor: 'bg-green-500/10', textColor: 'text-green-500', borderColor: 'border-green-500/30', icon: '✅' },
  REJECTED: { label: 'Abgelehnt', color: 'red', bgColor: 'bg-red-500/10', textColor: 'text-red-500', borderColor: 'border-red-500/30', icon: '❌' },
} as const;

// Feature Request Category with Display Info
export const FEATURE_REQUEST_CATEGORY = {
  UI_UX: { label: 'UI/UX', icon: '🎨', color: 'purple', bgColor: 'bg-purple-500/10', textColor: 'text-purple-500', borderColor: 'border-purple-500/30' },
  NEW_FEATURE: { label: 'Neues Feature', icon: '✨', color: 'cyan', bgColor: 'bg-cyan-500/10', textColor: 'text-cyan-500', borderColor: 'border-cyan-500/30' },
  IMPROVEMENT: { label: 'Verbesserung', icon: '📈', color: 'green', bgColor: 'bg-green-500/10', textColor: 'text-green-500', borderColor: 'border-green-500/30' },
  BUG_FIX: { label: 'Bugfix', icon: '🐛', color: 'red', bgColor: 'bg-red-500/10', textColor: 'text-red-500', borderColor: 'border-red-500/30' },
  PERFORMANCE: { label: 'Performance', icon: '⚡', color: 'yellow', bgColor: 'bg-yellow-500/10', textColor: 'text-yellow-500', borderColor: 'border-yellow-500/30' },
  OTHER: { label: 'Sonstiges', icon: '📦', color: 'gray', bgColor: 'bg-gray-500/10', textColor: 'text-gray-500', borderColor: 'border-gray-500/30' },
} as const;
